import { STR } from '../i18n';

/**
 * Presentation-only helpers. Nothing here invents a fact: a missing timestamp renders as an
 * em dash, never as a guess, and never as "never signed in" — this app does not record that.
 */

/**
 * Day-first, short month, four-digit year — the demos' format, which is unambiguous in a way
 * a numeric date is not. Pinned rather than locale-derived so every operator reading the
 * same screen reads the same date.
 */
const DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function parse(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(iso: string | null | undefined): string {
  const date = parse(iso);
  return date === null ? STR.common.dash : DATE.format(date);
}

export function formatDateTime(iso: string | null | undefined): string {
  const date = parse(iso);
  return date === null ? STR.common.dash : DATE_TIME.format(date);
}

/**
 * `<input type="datetime-local">` speaks `YYYY-MM-DDTHH:mm` with NO offset — the operator's
 * own wall clock. The API refuses that outright (it wants ISO-8601 WITH an offset), so these
 * two convert in each direction and nowhere else does.
 *
 * `isoToLocalInput` is the read half: it prints the instant in the DEVICE's zone, which is
 * what the operator typed it in and what they will read it back as. Both return '' / null
 * for anything unparseable rather than a guess.
 */
export function isoToLocalInput(iso: string | null | undefined): string {
  const date = parse(iso);
  if (date === null) return '';
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The write half. Null means "the field is empty", which is a legitimate value to send. */
export function localInputToIso(value: string): string | null {
  if (value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** Calendar days apart, not 24-hour blocks — `Math.round` absorbs DST's short/long days. */
function calendarDaysBetween(earlier: Date, later: Date): number {
  return Math.round((startOfDay(later) - startOfDay(earlier)) / MS_PER_DAY);
}

/**
 * The audit log's relative stamp, in the design of record's own steps: minutes, then hours
 * within the same calendar day, then "Yesterday", then whole days.
 *
 * The day boundary is CALENDAR-based on purpose: 23:50 last night is "Yesterday" even though
 * it is nine hours ago, because that is how somebody reading a log thinks about it. A
 * timestamp in the future (clock skew between the server and this device) reads as "Just now"
 * rather than as a negative age — the one thing it must never do is claim a duration
 * backwards.
 */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  const date = parse(iso);
  if (date === null) return STR.common.dash;

  const minutes = Math.floor((now.getTime() - date.getTime()) / MS_PER_MINUTE);
  if (minutes < 1) return STR.audit.time.justNow;
  if (minutes < 60) return STR.audit.time.minutesAgo(minutes);

  const days = calendarDaysBetween(date, now);
  if (days <= 0) return STR.audit.time.hoursAgo(Math.floor(minutes / 60));
  if (days === 1) return STR.audit.time.yesterday;
  return STR.audit.time.daysAgo(days);
}

/**
 * Audit metadata is stored verbatim, so its keys arrive as the server wrote them
 * (`sessionVersion`, `applicationKey`, `ip`). This turns one into something readable without
 * a dictionary — the keys are DATA, not copy, and a hand-maintained label list would go stale
 * the moment a new verb records a new field.
 */
const KEY_ACRONYMS: Record<string, string> = {
  id: 'ID',
  ip: 'IP',
  url: 'URL',
  uri: 'URI',
  mfa: 'MFA',
};

export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/gu, '$1 $2')
    .replace(/[_-]+/gu, ' ')
    .trim()
    .split(/\s+/u)
    .filter((word) => word !== '');
  if (words.length === 0) return key;

  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      const acronym = KEY_ACRONYMS[lower];
      if (acronym !== undefined) return acronym;
      return index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : lower;
    })
    .join(' ');
}

/** One metadata value as a line of text. Never a guess: absent stays an em dash. */
export function formatMetadataValue(value: unknown): string {
  if (value === null || value === undefined) return STR.common.dash;
  if (typeof value === 'string') return value === '' ? STR.common.dash : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.length === 0
      ? STR.common.dash
      : value.map((item: unknown) => formatMetadataValue(item)).join(', ');
  }
  return JSON.stringify(value);
}

/**
 * The launcher's greeting. The part of day is decided here — presentation, from the device's
 * own clock, never a server fact — and the wording comes from `i18n.ts`, which holds the
 * whole sentence per part of day rather than fragments the component would glue together.
 *
 * A person whose name is blank is greeted without one instead of with an empty gap.
 */
export function greetingFor(name: string, now: Date = new Date()): string {
  const hour = now.getHours();
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const first = name.trim().split(/\s+/u)[0] ?? '';
  return first === ''
    ? STR.launcher.greeting.anonymous[part]
    : STR.launcher.greeting[part](first);
}

/** Up to two initials, for the avatar squares. Empty name yields an empty square. */
export function initials(name: string): string {
  return name
    .split(/\s+/u)
    .filter((part) => part !== '')
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}
